'use client';

import cx from 'classix';
import { formatDistance } from 'date-fns';
// import { type EmblaCarouselType } from 'embla-carousel';
import useEmblaCarousel from 'embla-carousel-react';
import { useParams } from 'next/navigation';
// import Image from 'next/image';
import {
  useLocale,
  useTranslations,
} from 'next-intl';
import {
  type MouseEvent,
  useState,
} from 'react';
import { FaHeart } from 'react-icons/fa6';

// import {
//   useCallback,
//   useEffect,
//   useState,
// } from 'react';
import {
  NextButton,
  PrevButton,
  useCarouselArrows,
} from '@/features/listing/components/listing-card/listing-carousel-arrows/use-carousel-arrows';
import ListingCarouselSlide
  from '@/features/listing/components/listing-card/listing-carousel-slide/listing-carousel-slide';
import {
  LISTING_URL_PART_VARIANTS,
  LISTINGS_TYPES,
} from '@/features/listing/constants/listing-constants';
import createListingBookmark from '@/features/listing/requests/create-listing-bookmark';
import deleteListingBookmark from '@/features/listing/requests/delete-listing-bookmark';
import {
  type Listing,
  type ListingUrl,
} from '@/features/listing/types/listing-types';
import useAppFlyerLink from '@/features/roomster-app/hooks/use-app-flyer-link';
import { getUserBearerCookie } from '@/features/user/cookies/cookie-user-bearer';
import { useRouter } from '@/i18n/routing';
import getDateFnsLocale from '@/libs/date-fns/get-date-fns-locale';
import { ROUTER_LINKS } from '@/shared/constants/links-constants';
import { type ImageThumbnail } from '@/shared/types/image-thumbnail-types';

import styles from './listing-carousel.module.scss';

interface ListingCarouselProps {
  images: ImageThumbnail[];
  imagesAlt: string;
  isNew: Listing['isNew'];
  isBookmarked: Listing['isBookmarked'];
  listingRefreshedDate: Listing['refreshed'];
  listingId: Listing['id'];
}

export default function ListingCarousel(props: ListingCarouselProps) {
  const {
    images,
    imagesAlt,
    isBookmarked,
    isNew,
    listingId,
    listingRefreshedDate,
  } = props;

  const listingLabelsTranslations = useTranslations('listing.labels');

  const locale = useLocale();
  const router = useRouter();
  const params = useParams();
  const appFlyerMobileBookmarkLink = useAppFlyerLink('mobile', 'mobileListingBookmark', ROUTER_LINKS.phoneAuth);

  const [isListingBookmarked,
    setIsListingBookmarked] = useState<boolean>(isBookmarked);

  // const [slidesInView,
  //   setSlidesInView] = useState<number[]>([]);

  const [emblaRef,
    emblaApi] = useEmblaCarousel({ align: 'start', loop: true });

  const relativeRefreshDate = formatDistance(
    listingRefreshedDate,
    new Date(),
    {
      addSuffix: true,
      locale: getDateFnsLocale({ locale, region: null }),
    },
  );

  const handleBookmarkClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    event.preventDefault();

    const loggedUser = getUserBearerCookie();

    if (loggedUser) {
      const currentListingType = LISTING_URL_PART_VARIANTS[params.listingType as ListingUrl];
      const currentListingTypeId = LISTINGS_TYPES[currentListingType];

      if (isListingBookmarked) {
        setIsListingBookmarked(false);
        deleteListingBookmark(listingId, currentListingTypeId)
          .catch(() => null);
      } else {
        setIsListingBookmarked(true);
        createListingBookmark(listingId, currentListingTypeId)
          .catch(() => null);
      }
    } else {
      router.push(appFlyerMobileBookmarkLink);
    }
  };

  const {
    nextBtnDisabled,
    onNextButtonClick,
    onPrevButtonClick,
    prevBtnDisabled,
  } = useCarouselArrows(emblaApi);

  // const updateSlidesInView = useCallback((api: EmblaCarouselType) => {
  //   setSlidesInView((slidesInViewState) => {
  //     if (slidesInViewState.length === api.slideNodes().length) {
  //       api.off('slidesInView', updateSlidesInView);
  //     }
  //     const inView = api
  //       .slidesInView()
  //       .filter((index) => !slidesInViewState.includes(index));
  //     return [...slidesInViewState, ...inView];
  //   });
  // }, []);

  // useEffect(() => {
  //   if (!emblaApi) return;
  //
  //   updateSlidesInView(emblaApi);
  //   emblaApi.on('slidesInView', updateSlidesInView);
  //   emblaApi.on('reInit', updateSlidesInView);
  // }, [emblaApi, updateSlidesInView]);

  // eslint-disable-next-line @typescript-eslint/no-unsafe-call

  // {images.length === 1 ? (
  //     <Image
  //         className={cx(styles.embla__slide__img, styles.embla__lazyLoad__img)}
  //         src={images[0].path}
  //         alt={imagesAlt}
  //         fill
  //         priority
  //         loading="eager"
  //         quality={25}
  //     />
  // ) : (

  return (
    <section className={styles.embla}>
      <button
        className={styles.embla_bookmark}
        aria-label="favorite"
        onClick={handleBookmarkClick}
      >
        <FaHeart className={cx(styles.embla_bookmarkIcon, isListingBookmarked && styles.embla_bookmarkIcon__isBookmarked)} />
      </button>

      {isNew && (
        <div className={styles.embla_isNew}>
          {listingLabelsTranslations('new')}
        </div>
      )}

      <div className={styles.embla_relativeDate}>
        {relativeRefreshDate}
      </div>

      <div className={styles.embla__viewport} ref={emblaRef}>
        <div className={styles.embla__container}>
          {images.map((image, index) => (
            <ListingCarouselSlide
              index={index}
              key={image.id}
              src={image.path}
              alt={imagesAlt}
            />
            // index === 0 ? (
            //   <div key={image.id} className={styles.embla__slide}>
            //     <Image
            //       className={cx(styles.embla__slide__img)}
            //       src={image.path}
            //       alt={imagesAlt}
            //       fill
            //       priority
            //       quality={25}
            //     />
            //   </div>
            // ) : (
            //   <ListingCarouselSlide
            //       index={index}
            //     key={image.id}
            //     src={image.path}
            //     alt={imagesAlt}
            //   />
            // )
          ))}
        </div>
      </div>

      {images.length > 1 && (
        <div className={styles.embla__buttons}>
          <PrevButton onClick={onPrevButtonClick} disabled={prevBtnDisabled} />
          <NextButton onClick={onNextButtonClick} disabled={nextBtnDisabled} />
        </div>
      )}

    </section>
  );
}
